import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline email capture for the launch waitlist.
// variant: "light" (default, on light bg) | "dark" (on dark/brand bg)
export const WaitlistForm = ({ source = "website", variant = "light", className = "" }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email, source });
      toast.success(data.message);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const dark = variant === "dark";

  if (done) {
    return (
      <div
        data-testid="waitlist-success"
        className={`flex items-center gap-3 rounded-2xl border px-5 py-4 ${
          dark ? "border-white/20 bg-white/10 text-white" : "border-greenaccent/30 bg-emerald-50 text-ink"
        } ${className}`}
      >
        <CheckCircle2 className="h-5 w-5 shrink-0 text-greenaccent" />
        <span className="text-sm font-medium">You're on the list — we'll email you the moment we launch.</span>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="waitlist-form"
      className={`flex flex-col gap-3 sm:flex-row ${className}`}
    >
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email address"
        data-testid="waitlist-email-input"
        aria-label="Email address"
        className={`h-[52px] flex-1 rounded-full border px-6 py-3.5 text-sm outline-none transition-colors duration-200 ${
          dark
            ? "border-white/20 bg-white/10 text-white placeholder:text-white/50 focus:border-greenaccent"
            : "border-slate-200 bg-white text-ink placeholder:text-slate-400 focus:border-brand"
        }`}
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="waitlist-submit-btn"
        className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-[background-color,transform] duration-200 hover:-translate-y-0.5 disabled:opacity-60 ${
          dark ? "bg-greenaccent text-white hover:bg-greenaccent-dark" : "bg-brand text-white hover:bg-brand-dark"
        }`}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        Join the waitlist
        {!loading ? <ArrowRight className="h-4 w-4" /> : null}
      </button>
    </form>
  );
};

export default WaitlistForm;
